import { Layer } from './Layer';
import { ThresholdOptimizer } from '../filters/ThresholdOptimizer';

export interface BoundarySegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export class SelectionManager {
  public active: boolean = false;
  public shape: 'rect' | 'ellipse' | 'mask' = 'rect';
  public x: number = 0;
  public y: number = 0;
  public width: number = 0;
  public height: number = 0;
  // Binary mask: width * height bytes (255 = selected, 0 = unselected)
  public mask: Uint8Array | null = null;
  public maskWidth: number = 0;
  public maskHeight: number = 0;

  // Visual overlay controls
  public showOverlayTint: boolean = true;
  private overlayCanvas: HTMLCanvasElement | null = null;
  private boundarySegments: BoundarySegment[] = [];

  constructor() {}

  public setRect(x: number, y: number, width: number, height: number, canvasWidth: number, canvasHeight: number) {
    const rx = Math.max(0, Math.min(x, x + width));
    const ry = Math.max(0, Math.min(y, y + height));
    const rw = Math.min(canvasWidth - rx, Math.abs(width));
    const rh = Math.min(canvasHeight - ry, Math.abs(height));

    if (rw <= 0 || rh <= 0) {
      this.deselect();
      return;
    }

    this.active = true;
    this.shape = 'rect';
    this.x = Math.round(rx);
    this.y = Math.round(ry);
    this.width = Math.round(rw);
    this.height = Math.round(rh);

    // Create mask
    this.maskWidth = canvasWidth;
    this.maskHeight = canvasHeight;
    this.mask = new Uint8Array(canvasWidth * canvasHeight);
    for (let py = this.y; py < this.y + this.height; py++) {
      for (let px = this.x; px < this.x + this.width; px++) {
        this.mask[py * canvasWidth + px] = 255;
      }
    }

    this.updateOverlayCache(canvasWidth, canvasHeight);
  }

  public setEllipse(x: number, y: number, width: number, height: number, canvasWidth: number, canvasHeight: number) {
    const rx = Math.max(0, Math.min(x, x + width));
    const ry = Math.max(0, Math.min(y, y + height));
    const rw = Math.min(canvasWidth - rx, Math.abs(width));
    const rh = Math.min(canvasHeight - ry, Math.abs(height));

    if (rw <= 0 || rh <= 0) {
      this.deselect();
      return;
    }

    this.active = true;
    this.shape = 'ellipse';
    this.x = Math.round(rx);
    this.y = Math.round(ry);
    this.width = Math.round(rw);
    this.height = Math.round(rh);

    this.maskWidth = canvasWidth;
    this.maskHeight = canvasHeight;
    this.mask = new Uint8Array(canvasWidth * canvasHeight);

    const cx = this.x + this.width / 2;
    const cy = this.y + this.height / 2;
    const a = this.width / 2;
    const b = this.height / 2;

    for (let py = this.y; py < this.y + this.height; py++) {
      for (let px = this.x; px < this.x + this.width; px++) {
        const dx = (px - cx) / a;
        const dy = (py - cy) / b;
        if (dx * dx + dy * dy <= 1) {
          this.mask[py * canvasWidth + px] = 255;
        }
      }
    }

    this.updateOverlayCache(canvasWidth, canvasHeight);
  }

  /**
   * Magic Wand Selection: flood selects pixels matching target color within tolerance
   */
  public magicWandSelect(
    sourceCtx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    canvasWidth: number,
    canvasHeight: number,
    tolerance: number = 32,
    contiguous: boolean = true,
    autoTolerance: boolean = false
  ): { usedTolerance: number; isAuto: boolean } {
    startX = Math.floor(startX);
    startY = Math.floor(startY);
    if (startX < 0 || startX >= canvasWidth || startY < 0 || startY >= canvasHeight) {
      return { usedTolerance: tolerance, isAuto: autoTolerance };
    }

    const floodResult = ThresholdOptimizer.smartFlood(
      sourceCtx,
      canvasWidth,
      canvasHeight,
      [{ x: startX, y: startY }],
      {
        autoTolerance,
        manualTolerance: tolerance,
        contiguous
      }
    );

    if (floodResult.selectedCount === 0) {
      this.deselect();
      return { usedTolerance: floodResult.usedTolerance, isAuto: floodResult.isAuto };
    }

    const mask = floodResult.mask;
    let minX = canvasWidth;
    let maxX = 0;
    let minY = canvasHeight;
    let maxY = 0;

    for (let y = 0; y < canvasHeight; y++) {
      const row = y * canvasWidth;
      for (let x = 0; x < canvasWidth; x++) {
        if (mask[row + x] === 255) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    this.active = true;
    this.shape = 'mask';
    this.mask = mask;
    this.maskWidth = canvasWidth;
    this.maskHeight = canvasHeight;
    this.x = minX;
    this.y = minY;
    this.width = Math.max(1, maxX - minX + 1);
    this.height = Math.max(1, maxY - minY + 1);

    this.updateOverlayCache(canvasWidth, canvasHeight);
    return { usedTolerance: floodResult.usedTolerance, isAuto: floodResult.isAuto };
  }

  /**
   * Precomputes the cached visual overlay canvas and boundary segments for 60fps rendering
   */
  public updateOverlayCache(canvasWidth: number, canvasHeight: number) {
    this.boundarySegments = [];

    if (!this.active || !this.mask) {
      this.overlayCanvas = null;
      return;
    }

    if (!this.overlayCanvas) {
      this.overlayCanvas = document.createElement('canvas');
    }
    this.overlayCanvas.width = canvasWidth;
    this.overlayCanvas.height = canvasHeight;

    const ctx = this.overlayCanvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const w = this.maskWidth;
    const h = this.maskHeight;
    const mask = this.mask;

    // 1. Build translucent tint overlay
    const imgData = ctx.createImageData(canvasWidth, canvasHeight);
    const data = imgData.data;

    const startY = Math.max(0, this.y);
    const endY = Math.min(h, this.y + this.height);
    const startX = Math.max(0, this.x);
    const endX = Math.min(w, this.x + this.width);

    for (let y = startY; y < endY; y++) {
      const rowOffset = y * w;
      for (let x = startX; x < endX; x++) {
        const idx = rowOffset + x;
        if (mask[idx] === 255) {
          const pIdx = idx * 4;
          data[pIdx] = 14;     // R (Vibrant sky blue)
          data[pIdx + 1] = 165; // G
          data[pIdx + 2] = 233; // B
          data[pIdx + 3] = 75;  // Alpha (~30% opacity)
        }
      }
    }
    ctx.putImageData(imgData, 0, 0);

    // 2. Precompute merged horizontal boundary segments
    for (let y = Math.max(0, this.y - 1); y <= Math.min(h, this.y + this.height); y++) {
      let segStart = -1;
      for (let x = Math.max(0, this.x - 1); x <= Math.min(w, this.x + this.width); x++) {
        const topSel = (y > 0 && x < w) ? mask[(y - 1) * w + x] === 255 : false;
        const botSel = (y < h && x < w) ? mask[y * w + x] === 255 : false;
        const isEdge = topSel !== botSel;
        if (isEdge) {
          if (segStart === -1) segStart = x;
        } else {
          if (segStart !== -1) {
            this.boundarySegments.push({ x1: segStart, y1: y, x2: x, y2: y });
            segStart = -1;
          }
        }
      }
      if (segStart !== -1) {
        this.boundarySegments.push({ x1: segStart, y1: y, x2: Math.min(w, this.x + this.width + 1), y2: y });
      }
    }

    // 3. Precompute merged vertical boundary segments
    for (let x = Math.max(0, this.x - 1); x <= Math.min(w, this.x + this.width); x++) {
      let segStart = -1;
      for (let y = Math.max(0, this.y - 1); y <= Math.min(h, this.y + this.height); y++) {
        const leftSel = (x > 0 && y < h) ? mask[y * w + (x - 1)] === 255 : false;
        const rightSel = (x < w && y < h) ? mask[y * w + x] === 255 : false;
        const isEdge = leftSel !== rightSel;
        if (isEdge) {
          if (segStart === -1) segStart = y;
        } else {
          if (segStart !== -1) {
            this.boundarySegments.push({ x1: x, y1: segStart, x2: x, y2: y });
            segStart = -1;
          }
        }
      }
      if (segStart !== -1) {
        this.boundarySegments.push({ x1: x, y1: segStart, x2: x, y2: Math.min(h, this.y + this.height + 1) });
      }
    }
  }

  public selectAll(canvasWidth: number, canvasHeight: number) {
    this.active = true;
    this.shape = 'rect';
    this.x = 0;
    this.y = 0;
    this.width = canvasWidth;
    this.height = canvasHeight;
    this.maskWidth = canvasWidth;
    this.maskHeight = canvasHeight;
    this.mask = new Uint8Array(canvasWidth * canvasHeight);
    this.mask.fill(255);
    this.updateOverlayCache(canvasWidth, canvasHeight);
  }

  public deselect() {
    this.active = false;
    this.mask = null;
    this.width = 0;
    this.height = 0;
    this.boundarySegments = [];
    this.overlayCanvas = null;
  }

  public invert(canvasWidth: number, canvasHeight: number) {
    if (!this.active || !this.mask) {
      this.selectAll(canvasWidth, canvasHeight);
      return;
    }

    let minX = canvasWidth;
    let maxX = 0;
    let minY = canvasHeight;
    let maxY = 0;
    let anySelected = false;

    for (let y = 0; y < canvasHeight; y++) {
      for (let x = 0; x < canvasWidth; x++) {
        const idx = y * canvasWidth + x;
        const newVal = this.mask[idx] === 255 ? 0 : 255;
        this.mask[idx] = newVal;
        if (newVal === 255) {
          anySelected = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!anySelected) {
      this.deselect();
    } else {
      this.x = minX;
      this.y = minY;
      this.width = maxX - minX + 1;
      this.height = maxY - minY + 1;
      this.updateOverlayCache(canvasWidth, canvasHeight);
    }
  }

  /**
   * Expands selection mask outward by delta pixels (dilation)
   */
  public expand(delta: number, canvasWidth: number, canvasHeight: number) {
    if (!this.active || !this.mask) return;
    const w = this.maskWidth;
    const h = this.maskHeight;
    const oldMask = new Uint8Array(this.mask);
    const newMask = new Uint8Array(oldMask);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (oldMask[y * w + x] === 255) {
          for (let dy = -delta; dy <= delta; dy++) {
            const ny = y + dy;
            if (ny < 0 || ny >= h) continue;
            for (let dx = -delta; dx <= delta; dx++) {
              const nx = x + dx;
              if (nx < 0 || nx >= w) continue;
              if (dx * dx + dy * dy <= delta * delta) {
                newMask[ny * w + nx] = 255;
              }
            }
          }
        }
      }
    }

    this.mask = newMask;
    this.recomputeBounds(canvasWidth, canvasHeight);
    this.updateOverlayCache(canvasWidth, canvasHeight);
  }

  /**
   * Contracts selection mask inward by delta pixels (erosion)
   */
  public contract(delta: number, canvasWidth: number, canvasHeight: number) {
    if (!this.active || !this.mask) return;
    const w = this.maskWidth;
    const h = this.maskHeight;
    const oldMask = new Uint8Array(this.mask);
    const newMask = new Uint8Array(oldMask);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (oldMask[y * w + x] === 255) {
          let hasOutside = false;
          for (let dy = -delta; dy <= delta && !hasOutside; dy++) {
            const ny = y + dy;
            if (ny < 0 || ny >= h) { hasOutside = true; break; }
            for (let dx = -delta; dx <= delta; dx++) {
              const nx = x + dx;
              if (nx < 0 || nx >= w || oldMask[ny * w + nx] === 0) {
                hasOutside = true;
                break;
              }
            }
          }
          if (hasOutside) {
            newMask[y * w + x] = 0;
          }
        }
      }
    }

    this.mask = newMask;
    this.recomputeBounds(canvasWidth, canvasHeight);
    this.updateOverlayCache(canvasWidth, canvasHeight);
  }

  private recomputeBounds(canvasWidth: number, canvasHeight: number) {
    if (!this.mask) return;
    let minX = canvasWidth;
    let maxX = 0;
    let minY = canvasHeight;
    let maxY = 0;
    let any = false;

    for (let y = 0; y < canvasHeight; y++) {
      for (let x = 0; x < canvasWidth; x++) {
        if (this.mask[y * canvasWidth + x] === 255) {
          any = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!any) {
      this.deselect();
    } else {
      this.x = minX;
      this.y = minY;
      this.width = maxX - minX + 1;
      this.height = maxY - minY + 1;
    }
  }

  /**
   * Clears selected pixels in layer to completely transparent (alpha = 0)
   */
  public clearSelection(layer: Layer) {
    if (!this.active) return;
    const imgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
    const data = imgData.data;

    if (this.mask && this.maskWidth === layer.width && this.maskHeight === layer.height) {
      for (let i = 0; i < this.mask.length; i++) {
        if (this.mask[i] === 255) {
          data[i * 4 + 3] = 0; // alpha = 0
        }
      }
    } else {
      for (let py = this.y; py < this.y + this.height; py++) {
        for (let px = this.x; px < this.x + this.width; px++) {
          if (px >= 0 && px < layer.width && py >= 0 && py < layer.height) {
            const idx = (py * layer.width + px) * 4;
            data[idx + 3] = 0;
          }
        }
      }
    }
    layer.ctx.putImageData(imgData, 0, 0);
  }

  /**
   * Fills selected pixels with color
   */
  public fillSelection(layer: Layer, r: number, g: number, b: number, a: number = 255) {
    if (!this.active) return;
    const imgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
    const data = imgData.data;

    if (this.mask && this.maskWidth === layer.width && this.maskHeight === layer.height) {
      for (let i = 0; i < this.mask.length; i++) {
        if (this.mask[i] === 255) {
          const idx = i * 4;
          data[idx] = r;
          data[idx + 1] = g;
          data[idx + 2] = b;
          data[idx + 3] = a;
        }
      }
    } else {
      for (let py = this.y; py < this.y + this.height; py++) {
        for (let px = this.x; px < this.x + this.width; px++) {
          if (px >= 0 && px < layer.width && py >= 0 && py < layer.height) {
            const idx = (py * layer.width + px) * 4;
            data[idx] = r;
            data[idx + 1] = g;
            data[idx + 2] = b;
            data[idx + 3] = a;
          }
        }
      }
    }
    layer.ctx.putImageData(imgData, 0, 0);
  }

  /**
   * Renders selection visual overlay (tint + animated marching ants)
   */
  public renderOverlay(
    ctx: CanvasRenderingContext2D,
    zoom: number,
    panX: number,
    panY: number,
    animOffset: number
  ) {
    if (!this.active) return;

    ctx.save();

    // 1. Draw crystal clear translucent blue selection highlight tint
    if (this.showOverlayTint && this.overlayCanvas) {
      ctx.drawImage(
        this.overlayCanvas,
        panX,
        panY,
        this.maskWidth * zoom,
        this.maskHeight * zoom
      );
    }

    // 2. Draw high-contrast animated Marching Ants along boundaries
    if (this.boundarySegments.length > 0) {
      ctx.beginPath();
      for (let i = 0; i < this.boundarySegments.length; i++) {
        const seg = this.boundarySegments[i];
        ctx.moveTo(seg.x1 * zoom + panX, seg.y1 * zoom + panY);
        ctx.lineTo(seg.x2 * zoom + panX, seg.y2 * zoom + panY);
      }

      ctx.lineWidth = 1.5;
      ctx.lineCap = 'square';

      // Dark dash
      ctx.setLineDash([5, 5]);
      ctx.lineDashOffset = -animOffset;
      ctx.strokeStyle = '#000000';
      ctx.stroke();

      // Light contrasting dash
      ctx.lineDashOffset = -animOffset + 5;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
    } else if (this.shape === 'rect') {
      const sx = this.x * zoom + panX;
      const sy = this.y * zoom + panY;
      const sw = this.width * zoom;
      const sh = this.height * zoom;

      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 5]);
      ctx.lineDashOffset = -animOffset;
      ctx.strokeStyle = '#000000';
      ctx.strokeRect(sx, sy, sw, sh);

      ctx.lineDashOffset = -animOffset + 5;
      ctx.strokeStyle = '#ffffff';
      ctx.strokeRect(sx, sy, sw, sh);
    }

    ctx.restore();
  }
}
